import { Injectable } from '@angular/core';
import { getDateUtc } from '@wz-utilities/date.utilities';
import {
  arrayToSelectListItemArray,
  stringArrayToSelectListItemArray,
} from '@wz-utilities/form.utilities';
import { pickProperties } from '@wz-utilities/object.utilities';
import { PolicyModels } from 'models/policy.models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VehicleModels } from '../../../../models/vehicle.models';
import { projectVehicleFromVehicleSearch } from '../projectors/quotation-projector';
import { CarPickerApiService } from './api/car-picker-api.service';
import { ILogger } from './logger/ilogger';
import { LoggerService } from './logger/logger.service';

const SEARCH_PROPS: string[] = [
  'marque',
  'modele',
  'energie',
  'chevauxFiscaux',
  'carrosserie',
  'typeMine',
  'version',
];

@Injectable({
  providedIn: 'root',
})
export class CarPickerService {
  private logger: ILogger;

  constructor(
    private carPickerApiService: CarPickerApiService,
    logger: LoggerService,
  ) {
    this.logger = logger.getLogger('CarPickerService');
  }

  getMarques() {
    return this.carPickerApiService
      .getMarques()
      .pipe(map((v) => stringArrayToSelectListItemArray(v)));
  }

  getModeles(carSearch: VehicleModels.Search) {
    const request = pickProperties(carSearch, ...SEARCH_PROPS.slice(0, 1));
    this.logger.log('getModeles', request);
    return this.carPickerApiService
      .getModeles(request)
      .pipe(map((v) => stringArrayToSelectListItemArray(v)));
  }

  getEnergie(carSearch: VehicleModels.Search) {
    const request = pickProperties(carSearch, ...SEARCH_PROPS.slice(0, 2));
    this.logger.log('getEnergie', request);
    return this.carPickerApiService
      .getEnergie(request)
      .pipe(map((v) => stringArrayToSelectListItemArray(v)));
  }

  getChevauxFiscaux(carSearch: VehicleModels.Search) {
    const request = pickProperties(carSearch, ...SEARCH_PROPS.slice(0, 3));
    this.logger.log('getChevauxFiscaux', request);
    return this.carPickerApiService
      .getChevauxFiscaux(request)
      .pipe(map((v) => stringArrayToSelectListItemArray(v)));
  }

  getCarrosseries(carSearch: VehicleModels.Search) {
    const request = pickProperties(carSearch, ...SEARCH_PROPS.slice(0, 4));
    this.logger.log('getCarrosseries', request);
    return this.carPickerApiService
      .getCarrosseries(request)
      .pipe(map((v) => stringArrayToSelectListItemArray(v)));
  }

  getVersions(carSearch: VehicleModels.Search) {
    const request: VehicleModels.Search = pickProperties(
      carSearch,
      ...SEARCH_PROPS.slice(0, 6),
    );
    if (carSearch.dateMiseEnCirculation) {
      request.anneeMiseEnCirculation = getDateUtc(
        carSearch.dateMiseEnCirculation,
      ).getFullYear();
    }
    this.logger.log('getVersions', request);
    return this.carPickerApiService.getVersions(request).pipe(
      map((versions) =>
        arrayToSelectListItemArray(versions, (v) => ({
          value: v,
          text: this.getVersionText(v),
        })),
      ),
    );
  }

  getVersionText(v: VehicleModels.VersionWithIdentifiers): string {
    if (!v.version) {
      return 'Aucune version spécifiée';
    }

    if (v.version && v.typeMine) {
      return `${v.version} - ${v.typeMine}`;
    }

    return v.version;
  }

  getIdentifiers(carSearch: VehicleModels.Search) {
    this.logger.log('getCodeGta', carSearch);
    return this.carPickerApiService.getIdentifiers(carSearch);
  }

  getInformationsFromCodeGta(
    codeGta: string,
  ): Observable<PolicyModels.VehicleDetails> {
    return this.carPickerApiService
      .getInformationsFromCodeGta(codeGta)
      .pipe(map(projectVehicleFromVehicleSearch));
  }

  getInformationsFromTypeMine(
    typeMine: string,
  ): Observable<PolicyModels.VehicleDetails> {
    return this.carPickerApiService
      .getInformationsFromTypeMine(typeMine)
      .pipe(map(projectVehicleFromVehicleSearch));
  }

  getInformationsFromImmatriculation(
    immatriculation: string,
  ): Observable<PolicyModels.VehicleDetails> {
    return this.carPickerApiService
      .getInformationsFromImmatriculation(immatriculation)
      .pipe(map(projectVehicleFromVehicleSearch));
  }
}
